/// <amd-module name="Repos/Editor/Components/AnnotatedCodeEditor" />
import * as React from "react";
import { IAnnotationBlock } from "Repos/Common/Contracts/Editor";
import { IReadyableReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { VssComponent } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Observer } from "VSSUI/Observer";
import { Splitter, SplitterDirection, SplitterElementPosition } from "VSSUI/Splitter";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { AnnotationList } from "./AnnotationList";
import { CodeEditor, ICodeEditorProps } from "./CodeEditor";

const splitterSettingsKey = "Repos.AnnotatedCodeEditor.SplitterWidth";

export interface IAnnotatedCodeEditorProps extends ICodeEditorProps {
    annotationBlocks: IReadyableReadonlyObservableArray<IAnnotationBlock>;
}

export class AnnotatedCodeEditor extends VssComponent<IAnnotatedCodeEditorProps, { editorLineHeight: number }> {
    private editor = new ObservableValue<monaco.editor.ICodeEditor | null>(null);
    private annotationsList: AnnotationList | null = null;
    private selectedLine = new ObservableValue(1);
    private timerManagement: TimerManagement;
    private debouncedAnnotationsScrollChanged: (scrollTop: number) => void;
    private annotationPaneWidth = new ObservableValue(0);

    constructor(props: IAnnotatedCodeEditorProps) {
        super(props);

        this.editor.subscribe(this.onEditorChanged);

        this.timerManagement = new TimerManagement();
        this.debouncedAnnotationsScrollChanged = this.timerManagement.debounce(this.annotationsScrollChanged, 50, { maxWait: 200 });

        this.state = { editorLineHeight: 16 };
    }

    public render() {
        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    this.annotationPaneWidth.value =
                        screenSizeProps.screenSize > ScreenSize.xsmall ? parseInt("" + tryGetLocalStorageValue(splitterSettingsKey)) || 300 : 100;
                    return (
                        <Splitter
                            fixedElement={SplitterElementPosition.Near}
                            splitterDirection={SplitterDirection.Vertical}
                            initialFixedSize={this.annotationPaneWidth.value}
                            nearElementClassName="flex-column"
                            farElementClassName="flex-column"
                            onFixedSizeChanged={newSize => {
                                this.annotationPaneWidth.value = newSize;
                                trySetLocalStorageValue(splitterSettingsKey, "" + newSize);
                            }}
                            onRenderNearElement={this.onRenderNearElement}
                            onRenderFarElement={this.onRenderFarElement}
                        />
                    );
                }}
            </ScreenSizeObserver>
        );
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this.timerManagement.dispose();
    }

    private editorScrollChanged = (scrollEvent: monaco.IScrollEvent) => {
        // Avoid scrolling for infra-pixel diffs, this uses to happen when page is zoomed (repro with 110%)
        if (this.annotationsList && Math.abs(this.annotationsList.getScrollTop() - scrollEvent.scrollTop) >= 1) {
            this.annotationsList.scrollTo(scrollEvent.scrollTop);
        }
    };

    private annotationsScrollChanged = (scrollTop: number) => {
        const editor = this.editor.value;
        if (editor && Math.abs(editor.getScrollTop() - scrollTop) >= 1) {
            editor.setScrollTop(scrollTop);
        }
    };

    /**
     * Set up the scroll event listener on the monaco editor
     */
    private onEditorChanged = (editor: monaco.editor.ICodeEditor | null) => {
        if (editor) {
            this.setState({ editorLineHeight: editor.getOption(monaco.editor.EditorOption.lineHeight) });
            editor.onDidScrollChange(this.editorScrollChanged);
            editor.onDidChangeCursorPosition(this.editorCursorPositionChanged);
        }
    };

    private editorCursorPositionChanged = (event: monaco.editor.ICursorPositionChangedEvent) => {
        if (this.selectedLine.value !== event.position.lineNumber) {
            this.selectedLine.value = event.position.lineNumber;
        }
    };

    private onRenderFarElement = () => {
        return (
            <CodeEditor
                content={this.props.content}
                editorRef={editor => {
                    if (this.editor.value !== editor) {
                        this.editor.value = editor;
                    }
                }}
                forceSingleLineMode={true}
                getDownloadUrl={this.props.getDownloadUrl}
                initialScrollPosition={this.props.initialScrollPosition}
                onLoaded={this.props.onLoaded}
                onScrollChanged={this.props.onScrollChanged}
                scrollToLine={this.props.scrollToLine}
            />
        );
    };

    private onRenderNearElement = () => {
        return (
            <Observer editor={this.editor}>
                {(props: { editor: monaco.editor.ICodeEditor }) => {
                    if (props.editor) {
                        const height = props.editor.getDomNode()!.clientHeight;
                        return (
                            <AnnotationList
                                annotationBlocks={this.props.annotationBlocks}
                                initialScrollPosition={this.props.initialScrollPosition}
                                onScroll={this.debouncedAnnotationsScrollChanged}
                                lineHeight={this.state.editorLineHeight}
                                height={height}
                                ref={list => {
                                    this.annotationsList = list;
                                    if (list && this.editor.value) {
                                        list.scrollTo(this.editor.value.getScrollTop());
                                    }
                                }}
                                selectedLine={this.selectedLine}
                            />
                        );
                    } else {
                        return null;
                    }
                }}
            </Observer>
        );
    };
}
